import { CodeBlock, Alert, Table } from '../../components/docs';
import styles from '../../components/docs/docs.module.css';

export default function Errors() {
	return (
		<div className={styles.docsPage}>
			<h1>Error Codes Reference</h1>
			<p className={styles.lead}>
				Ringfort APIs use consistent error responses across all endpoints. This
				guide covers the error format, HTTP status codes, and a complete
				reference of all error codes to help you handle errors gracefully in
				your integration.
			</p>

			{/* Error Response Format */}
			<h2>Error Response Format</h2>
			<p>
				All error responses follow a consistent JSON structure that includes an
				error code, human-readable message, and optional additional details.
			</p>

			<CodeBlock language="json" title="Error Response Structure">
				{`{
  "success": false,
  "error": {
    "code": "validation/invalid_amount",
    "message": "Bet amount must be a positive integer string",
    "details": {
      "field": "amount",
      "provided": "-100",
      "expected": "Positive integer string (e.g., '1000')"
    },
    "requestId": "req_abc123xyz789",
    "timestamp": "2024-01-15T14:30:00Z"
  }
}`}
			</CodeBlock>

			<h3>Error Object Fields</h3>
			<Table
				columns={[
					{ key: 'field', header: 'Field' },
					{ key: 'type', header: 'Type' },
					{ key: 'description', header: 'Description' },
				]}
				data={[
					{
						field: 'code',
						type: 'string',
						description:
							'Unique error code in format category/specific_error. Use this for programmatic error handling.',
					},
					{
						field: 'message',
						type: 'string',
						description:
							'Human-readable error message. Safe to display to end users in most cases.',
					},
					{
						field: 'details',
						type: 'object',
						description:
							'Optional additional context about the error. Structure varies by error type.',
					},
					{
						field: 'requestId',
						type: 'string',
						description:
							'Unique request identifier for debugging. Include this when contacting support.',
					},
					{
						field: 'timestamp',
						type: 'string',
						description: 'ISO 8601 timestamp when the error occurred.',
					},
				]}
			/>

			{/* HTTP Status Codes */}
			<h2>HTTP Status Codes</h2>
			<p>
				Ringfort APIs use standard HTTP status codes to indicate the success or
				failure of requests.
			</p>

			<h3>Success Codes</h3>
			<Table
				columns={[
					{ key: 'code', header: 'Code' },
					{ key: 'name', header: 'Name' },
					{ key: 'description', header: 'When Used' },
				]}
				data={[
					{
						code: '200',
						name: 'OK',
						description:
							'Request succeeded. Response body contains the requested data.',
					},
					{
						code: '201',
						name: 'Created',
						description:
							'Resource created successfully. Response contains the new resource.',
					},
					{
						code: '204',
						name: 'No Content',
						description:
							'Request succeeded but no content to return. Used for DELETE operations.',
					},
				]}
			/>

			<h3>Client Error Codes</h3>
			<Table
				columns={[
					{ key: 'code', header: 'Code' },
					{ key: 'name', header: 'Name' },
					{ key: 'description', header: 'When Used' },
				]}
				data={[
					{
						code: '400',
						name: 'Bad Request',
						description:
							'Invalid request syntax, missing required fields, or validation errors.',
					},
					{
						code: '401',
						name: 'Unauthorized',
						description:
							'Missing, invalid, or expired authentication credentials.',
					},
					{
						code: '403',
						name: 'Forbidden',
						description:
							'Valid credentials but insufficient permissions for the requested action.',
					},
					{
						code: '404',
						name: 'Not Found',
						description: 'The requested resource does not exist.',
					},
					{
						code: '409',
						name: 'Conflict',
						description:
							'Request conflicts with current state (e.g., duplicate transaction ID).',
					},
					{
						code: '422',
						name: 'Unprocessable Entity',
						description:
							'Request is well-formed but contains semantic errors (e.g., insufficient balance).',
					},
					{
						code: '429',
						name: 'Too Many Requests',
						description: 'Rate limit exceeded. Check rate limit headers.',
					},
				]}
			/>

			<h3>Server Error Codes</h3>
			<Table
				columns={[
					{ key: 'code', header: 'Code' },
					{ key: 'name', header: 'Name' },
					{ key: 'description', header: 'When Used' },
				]}
				data={[
					{
						code: '500',
						name: 'Internal Server Error',
						description: 'Unexpected server error. Safe to retry with backoff.',
					},
					{
						code: '502',
						name: 'Bad Gateway',
						description:
							'Upstream service unavailable. Usually temporary, retry with backoff.',
					},
					{
						code: '503',
						name: 'Service Unavailable',
						description:
							'Service temporarily unavailable (maintenance, overload). Retry later.',
					},
					{
						code: '504',
						name: 'Gateway Timeout',
						description:
							'Upstream service timeout. May retry, but check if action completed.',
					},
				]}
			/>

			{/* Authentication Errors */}
			<h2>Authentication Errors</h2>
			<p>
				Errors related to API keys, session tokens, and authentication
				credentials.
			</p>

			<Table
				columns={[
					{ key: 'code', header: 'Error Code' },
					{ key: 'status', header: 'HTTP' },
					{ key: 'description', header: 'Description' },
					{ key: 'resolution', header: 'Resolution' },
				]}
				data={[
					{
						code: 'auth/missing_token',
						status: '401',
						description: 'No authentication token provided in request.',
						resolution: 'Include Authorization header with Bearer token.',
					},
					{
						code: 'auth/invalid_token',
						status: '401',
						description: 'Token is malformed or has invalid signature.',
						resolution: 'Verify token format and regenerate if corrupted.',
					},
					{
						code: 'auth/expired_token',
						status: '401',
						description: 'Authentication token has expired.',
						resolution: 'Request a new session token or refresh credentials.',
					},
					{
						code: 'auth/invalid_api_key',
						status: '401',
						description: 'API key is invalid or has been revoked.',
						resolution: 'Check API key in dashboard or generate new key.',
					},
					{
						code: 'auth/wrong_environment',
						status: '401',
						description:
							'Using test key in production or vice versa.',
						resolution: 'Use appropriate key for the environment.',
					},
					{
						code: 'auth/operator_suspended',
						status: '403',
						description: 'Operator account has been suspended.',
						resolution: 'Contact support to resolve account issues.',
					},
					{
						code: 'auth/insufficient_permissions',
						status: '403',
						description: 'Valid auth but lacking required permissions.',
						resolution: 'Request elevated permissions or use different credentials.',
					},
					{
						code: 'auth/ip_not_whitelisted',
						status: '403',
						description: 'Request from non-whitelisted IP address.',
						resolution: 'Add IP to whitelist in operator dashboard.',
					},
				]}
			/>

			<CodeBlock language="json" title="Example: Expired Token Error">
				{`{
  "success": false,
  "error": {
    "code": "auth/expired_token",
    "message": "Your session has expired. Please request a new game session.",
    "details": {
      "expiredAt": "2024-01-15T12:00:00Z",
      "tokenType": "session"
    },
    "requestId": "req_abc123"
  }
}`}
			</CodeBlock>

			{/* Validation Errors */}
			<h2>Validation Errors</h2>
			<p>Errors related to request parameters, body content, and data formats.</p>

			<Table
				columns={[
					{ key: 'code', header: 'Error Code' },
					{ key: 'status', header: 'HTTP' },
					{ key: 'description', header: 'Description' },
					{ key: 'resolution', header: 'Resolution' },
				]}
				data={[
					{
						code: 'validation/missing_field',
						status: '400',
						description: 'Required field is missing from request.',
						resolution: 'Include all required fields. Check details.field for missing field name.',
					},
					{
						code: 'validation/invalid_type',
						status: '400',
						description: 'Field value has wrong data type.',
						resolution: 'Provide correct type (string, number, boolean, etc.).',
					},
					{
						code: 'validation/invalid_format',
						status: '400',
						description: 'Field value does not match expected format.',
						resolution: 'Check expected format in documentation (e.g., ISO dates, currency codes).',
					},
					{
						code: 'validation/invalid_amount',
						status: '400',
						description: 'Amount is not a valid positive integer string.',
						resolution: 'Provide amount as positive integer string (e.g., "1000").',
					},
					{
						code: 'validation/amount_too_small',
						status: '400',
						description: 'Amount is below minimum allowed value.',
						resolution: 'Check minimum bet/transaction limits.',
					},
					{
						code: 'validation/amount_too_large',
						status: '400',
						description: 'Amount exceeds maximum allowed value.',
						resolution: 'Check maximum bet/transaction limits.',
					},
					{
						code: 'validation/invalid_currency',
						status: '400',
						description: 'Currency code is not supported.',
						resolution: 'Use supported currency codes (USD, EUR, BTC, ETH, etc.).',
					},
					{
						code: 'validation/invalid_game',
						status: '400',
						description: 'Game identifier is not recognized.',
						resolution: 'Use valid game ID: dice, plinko, coinflip, crash.',
					},
					{
						code: 'validation/invalid_target',
						status: '400',
						description: 'Dice target value is out of valid range.',
						resolution: 'Target must be between 0.01 and 99.99.',
					},
					{
						code: 'validation/invalid_condition',
						status: '400',
						description: 'Invalid win condition specified.',
						resolution: 'Use "under" or "over" for dice condition.',
					},
					{
						code: 'validation/invalid_rows',
						status: '400',
						description: 'Plinko rows value is not allowed.',
						resolution: 'Use 8, 10, 12, 14, or 16 rows.',
					},
					{
						code: 'validation/invalid_risk_level',
						status: '400',
						description: 'Risk level is not recognized.',
						resolution: 'Use "low", "medium", or "high".',
					},
				]}
			/>

			<CodeBlock language="json" title="Example: Validation Error with Details">
				{`{
  "success": false,
  "error": {
    "code": "validation/invalid_amount",
    "message": "Bet amount must be a positive integer string",
    "details": {
      "field": "amount",
      "provided": "10.50",
      "expected": "Positive integer string representing smallest currency unit",
      "example": "1050 (for $10.50 USD)"
    },
    "requestId": "req_def456"
  }
}`}
			</CodeBlock>

			{/* Game Errors */}
			<h2>Game Errors</h2>
			<p>
				Errors specific to game operations, betting, and game state management.
			</p>

			<Table
				columns={[
					{ key: 'code', header: 'Error Code' },
					{ key: 'status', header: 'HTTP' },
					{ key: 'description', header: 'Description' },
					{ key: 'resolution', header: 'Resolution' },
				]}
				data={[
					{
						code: 'game/session_not_found',
						status: '404',
						description: 'Game session does not exist.',
						resolution: 'Create a new session through operator API.',
					},
					{
						code: 'game/session_expired',
						status: '401',
						description: 'Game session has expired.',
						resolution: 'Request new session token from operator.',
					},
					{
						code: 'game/session_invalidated',
						status: '401',
						description: 'Session was manually invalidated.',
						resolution: 'Create new session for player to continue playing.',
					},
					{
						code: 'game/game_disabled',
						status: '403',
						description: 'This game is currently disabled for the operator.',
						resolution: 'Contact Ringfort support or enable game in settings.',
					},
					{
						code: 'game/bet_in_progress',
						status: '409',
						description: 'Cannot place bet while another bet is processing.',
						resolution: 'Wait for current bet to complete before placing new bet.',
					},
					{
						code: 'game/bet_not_found',
						status: '404',
						description: 'Bet ID does not exist.',
						resolution: 'Verify bet ID is correct.',
					},
					{
						code: 'game/bet_already_settled',
						status: '409',
						description: 'Bet has already been settled.',
						resolution: 'Cannot modify or cancel settled bets.',
					},
					{
						code: 'game/crash_round_not_active',
						status: '400',
						description: 'No active crash round to join or bet on.',
						resolution: 'Wait for next round to start.',
					},
					{
						code: 'game/crash_betting_closed',
						status: '400',
						description: 'Betting phase for crash round has ended.',
						resolution: 'Wait for next round to place bet.',
					},
					{
						code: 'game/crash_already_cashed_out',
						status: '409',
						description: 'Bet has already been cashed out.',
						resolution: 'Cannot cash out the same bet twice.',
					},
					{
						code: 'game/crash_already_crashed',
						status: '400',
						description: 'Round has already crashed.',
						resolution: 'Bet is lost. Wait for next round.',
					},
					{
						code: 'game/seed_rotation_pending',
						status: '409',
						description: 'Seed rotation in progress.',
						resolution: 'Wait for rotation to complete before placing bets.',
					},
				]}
			/>

			<CodeBlock language="json" title="Example: Crash Game Error">
				{`{
  "success": false,
  "error": {
    "code": "game/crash_already_crashed",
    "message": "The round has already crashed. Your bet was not cashed out in time.",
    "details": {
      "roundId": "round_crash_001",
      "crashPoint": 2.45,
      "yourBetId": "bet_crash_abc123",
      "autoCashout": null
    },
    "requestId": "req_ghi789"
  }
}`}
			</CodeBlock>

			{/* Wallet Errors */}
			<h2>Wallet Errors</h2>
			<p>
				Errors related to balances, transactions, deposits, and withdrawals.
			</p>

			<Table
				columns={[
					{ key: 'code', header: 'Error Code' },
					{ key: 'status', header: 'HTTP' },
					{ key: 'description', header: 'Description' },
					{ key: 'resolution', header: 'Resolution' },
				]}
				data={[
					{
						code: 'wallet/insufficient_balance',
						status: '422',
						description: 'Player does not have enough balance for this bet.',
						resolution: 'Reduce bet amount or deposit more funds.',
					},
					{
						code: 'wallet/player_not_found',
						status: '404',
						description: 'Player wallet does not exist.',
						resolution: 'Create session for player first to initialize wallet.',
					},
					{
						code: 'wallet/currency_mismatch',
						status: '400',
						description: 'Transaction currency does not match session currency.',
						resolution: 'Use consistent currency for all operations.',
					},
					{
						code: 'wallet/transaction_failed',
						status: '500',
						description: 'Wallet transaction could not be processed.',
						resolution: 'Retry the operation. If persists, contact support.',
					},
					{
						code: 'wallet/duplicate_transaction',
						status: '409',
						description: 'Transaction ID has already been used.',
						resolution: 'Use unique transaction ID for each request.',
					},
					{
						code: 'wallet/withdrawal_limit_exceeded',
						status: '422',
						description: 'Withdrawal exceeds configured limits.',
						resolution: 'Reduce amount or wait for limit reset.',
					},
					{
						code: 'wallet/deposit_limit_exceeded',
						status: '422',
						description: 'Deposit exceeds configured limits.',
						resolution: 'Reduce amount or adjust limits in settings.',
					},
					{
						code: 'wallet/callback_failed',
						status: '502',
						description: 'Seamless wallet callback to operator failed.',
						resolution: 'Check operator webhook endpoint is accessible.',
					},
					{
						code: 'wallet/callback_timeout',
						status: '504',
						description: 'Seamless wallet callback timed out.',
						resolution: 'Ensure webhook endpoint responds within 5 seconds.',
					},
					{
						code: 'wallet/callback_rejected',
						status: '422',
						description: 'Operator wallet rejected the transaction.',
						resolution: 'Check operator wallet logs for rejection reason.',
					},
				]}
			/>

			<CodeBlock language="json" title="Example: Insufficient Balance Error">
				{`{
  "success": false,
  "error": {
    "code": "wallet/insufficient_balance",
    "message": "Insufficient balance to place this bet",
    "details": {
      "required": "10000",
      "available": "5000",
      "currency": "USD",
      "shortfall": "5000"
    },
    "requestId": "req_jkl012"
  }
}`}
			</CodeBlock>

			{/* System Errors */}
			<h2>System Errors</h2>
			<p>
				Infrastructure and platform-level errors that are typically temporary.
			</p>

			<Table
				columns={[
					{ key: 'code', header: 'Error Code' },
					{ key: 'status', header: 'HTTP' },
					{ key: 'description', header: 'Description' },
					{ key: 'resolution', header: 'Resolution' },
				]}
				data={[
					{
						code: 'system/internal_error',
						status: '500',
						description: 'Unexpected internal server error.',
						resolution: 'Retry with exponential backoff. Contact support if persists.',
					},
					{
						code: 'system/service_unavailable',
						status: '503',
						description: 'Service temporarily unavailable.',
						resolution: 'Wait and retry. Check status page for maintenance.',
					},
					{
						code: 'system/database_error',
						status: '500',
						description: 'Database operation failed.',
						resolution: 'Retry the operation. Usually temporary.',
					},
					{
						code: 'system/rate_limited',
						status: '429',
						description: 'Too many requests. Rate limit exceeded.',
						resolution: 'Reduce request frequency. Check Retry-After header.',
					},
					{
						code: 'system/maintenance',
						status: '503',
						description: 'Platform is under scheduled maintenance.',
						resolution: 'Check status page for maintenance window end time.',
					},
					{
						code: 'system/region_unavailable',
						status: '503',
						description: 'Service unavailable in your region.',
						resolution: 'Try different region or contact support.',
					},
				]}
			/>

			<CodeBlock language="json" title="Example: Rate Limit Error">
				{`{
  "success": false,
  "error": {
    "code": "system/rate_limited",
    "message": "Rate limit exceeded. Please slow down your requests.",
    "details": {
      "limit": 100,
      "window": "1 minute",
      "retryAfter": 45
    },
    "requestId": "req_mno345"
  }
}`}
			</CodeBlock>

			{/* Error Handling Best Practices */}
			<h2>Error Handling Best Practices</h2>

			<Alert variant="info" title="Always Check success Field">
				Every response includes a <code>success</code> boolean. Always check
				this field before processing the response data.
			</Alert>

			<h3>Recommended Error Handling Pattern</h3>
			<CodeBlock language="typescript" title="TypeScript Error Handler">
				{`interface RingfortError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
  requestId: string;
}

interface RingfortResponse<T> {
  success: boolean;
  data?: T;
  error?: RingfortError;
}

async function placeBet(params: BetParams): Promise<BetResult> {
  const response = await fetch('/api/v1/game/dice/bet', {
    method: 'POST',
    headers: {
      'Authorization': \`Bearer \${sessionToken}\`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });

  const result: RingfortResponse<BetResult> = await response.json();

  if (!result.success) {
    const error = result.error!;

    // Handle specific error codes
    switch (error.code) {
      case 'wallet/insufficient_balance':
        showToast('Not enough balance. Please deposit more funds.');
        break;
      case 'auth/session_expired':
        // Redirect to get new session
        await refreshSession();
        break;
      case 'system/rate_limited':
        // Wait and retry
        const retryAfter = error.details?.retryAfter as number || 60;
        await delay(retryAfter * 1000);
        return placeBet(params);
      default:
        // Log for debugging
        console.error(\`Bet failed: \${error.code}\`, error);
        showToast(error.message);
    }

    throw new BetError(error);
  }

  return result.data!;
}`}
			</CodeBlock>

			<h3>Retry Recommendations</h3>
			<Table
				columns={[
					{ key: 'errorType', header: 'Error Type' },
					{ key: 'retry', header: 'Should Retry' },
					{ key: 'strategy', header: 'Strategy' },
				]}
				data={[
					{
						errorType: 'Authentication (401)',
						retry: 'No',
						strategy: 'Refresh credentials and retry once.',
					},
					{
						errorType: 'Validation (400)',
						retry: 'No',
						strategy: 'Fix request parameters before retrying.',
					},
					{
						errorType: 'Not Found (404)',
						retry: 'No',
						strategy: 'Resource does not exist.',
					},
					{
						errorType: 'Conflict (409)',
						retry: 'Maybe',
						strategy: 'Check current state before retrying with different params.',
					},
					{
						errorType: 'Rate Limited (429)',
						retry: 'Yes',
						strategy: 'Wait for Retry-After header duration, then retry.',
					},
					{
						errorType: 'Server Error (500)',
						retry: 'Yes',
						strategy: 'Exponential backoff: 1s, 2s, 4s, max 3 retries.',
					},
					{
						errorType: 'Unavailable (503)',
						retry: 'Yes',
						strategy: 'Check status page, retry with longer backoff.',
					},
					{
						errorType: 'Timeout (504)',
						retry: 'Maybe',
						strategy: 'May have succeeded. Check state before retrying.',
					},
				]}
			/>

			<h3>Exponential Backoff Implementation</h3>
			<CodeBlock language="typescript" title="Retry with Backoff">
				{`async function fetchWithRetry<T>(
  url: string,
  options: RequestInit,
  maxRetries = 3
): Promise<T> {
  let lastError: Error;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const response = await fetch(url, options);

      if (response.status === 429) {
        const retryAfter = parseInt(
          response.headers.get('Retry-After') || '60'
        );
        await delay(retryAfter * 1000);
        continue;
      }

      if (response.status >= 500) {
        const backoffMs = Math.pow(2, attempt) * 1000;
        await delay(backoffMs);
        continue;
      }

      return await response.json();
    } catch (error) {
      lastError = error as Error;
      const backoffMs = Math.pow(2, attempt) * 1000;
      await delay(backoffMs);
    }
  }

  throw lastError!;
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}`}
			</CodeBlock>

			{/* Next Steps */}
			<div className={styles.nextSteps}>
				<h2>Need Help?</h2>
				<ul>
					<li>
						<a href="/docs/api-operators">Operator API</a> - Session and balance
						management
					</li>
					<li>
						<a href="/docs/api-games">Game API</a> - Betting and verification
					</li>
					<li>
						<a href="/docs/testing-guide">Testing Guide</a> - Test your error
						handling
					</li>
					<li>
						<strong>Support:</strong> Include the <code>requestId</code> when
						contacting support
					</li>
				</ul>
			</div>
		</div>
	);
}
